import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileBarChart2 } from "lucide-react";
import { Session } from "./PatientFile";

interface ResultRate {
  successCount: number;
  failureCount: number;
}

type ExplorationResultRow = {
  label: string;
  success: boolean;
  failureCount: number;
  totalTime: number | null;
};

type ScoreRow = {
  label: string;
  score: string | number;
};

type RateRow = {
  label: string;
  rate: number;
  count: number;
};

type Row = ScoreRow | RateRow | ExplorationResultRow;

type Section = {
  title: string;
  headers: string[];
  rows: Row[];
  rowType: 'score' | 'rate' | 'exploration';
};


export const SingleSessionResultsPatientFile = ({
  session,
  fiable,
  accountUsed,
  sessionType,
  order,
}: {
  session: Session;
  fiable: boolean;
  sessionType : "BILAN" | "REEDUCATION"
  accountUsed : "PATIENT" | "CLINIQUE",
  order: number;
}) => {
  const textFiable = fiable ? "Fiable" : "Non fiable";
  const fiableClass = fiable ? "text-green-500" : "text-red-500";

  const parseRate = (rateData: string | null) => {
    if (!rateData) return { rate: 0, count: 0 };

    const { successCount, failureCount }: ResultRate = JSON.parse(rateData);
    const total = successCount + failureCount;
    const successRate = total > 0 ? (successCount / total) * 100 : 0;

    return { rate: successRate, count: total };
  };

  // Parse ZF and Bilan from position/reactionTime format
  const parsePositionData = (data: string | null) => {
    if (!data) return { rate: 0, count: 0 };
    try {
      const results = JSON.parse(data);
      let successCount = 0;
      let failureCount = 0;

      results.forEach((result: { position: string; reactionTime: string }) => {
        if (result.reactionTime !== "Trop long") {
          successCount++;
        } else {
          failureCount++;
        }
      });

      const total = successCount + failureCount;
      const successRate = total > 0 ? (successCount / total) * 100 : 0;

      return { rate: successRate, count: total };
    } catch (error) {
      console.error("Failed to parse JSON:", error);
      return { rate: 0, count: 0 };
    }
  };

  const parseBilanLecture = (lectureData: string | null): number | null => {
    if (!lectureData) return null;
    const timeInSeconds = parseFloat(lectureData);
    return isNaN(timeInSeconds) ? null : timeInSeconds;
  };

  const parseBilanExploration = (
    explorationData: string | null
  ): { success: boolean; failureCount: number; totalTime: number | null } => {
    if (!explorationData)
      return { success: false, failureCount: 0, totalTime: null };
    try {
      const parsedData = JSON.parse(explorationData);
      const success = parsedData.success;
      const results = parsedData.results;
      const totalTime =
        results.length > 0 ? results[results.length - 1].timeTaken : null;
      const failureCount = success ? results.length - 1 : results.length;

      return { success, failureCount, totalTime };
    } catch (error) {
      return { success: false, failureCount: 0, totalTime: null };
    }
  };

  const resultsZF = parsePositionData(session.resultsZF);
  const resultsBilan = parsePositionData(session.resultsBilan);
  const resultsZPCCF = parseRate(session.resultsZPCCF);
  const resultsZPFCF = parseRate(session.resultsZPFCF);
  const bilanLectureTime = parseBilanLecture(session.resultsBilanLecture);
  const resultsBilanExploration = parseBilanExploration(
    session.resultsExploration
  );

  const formatScore = (score: string | null) => {
    return score ? parseInt(score, 10) : "Non réalisé";
  };

  // Collect Rate Rows
  const rateRows: RateRow[] = [];

  if (resultsZF.count > 0) {
    rateRows.push({
      label: "Zone Frontière",
      rate: resultsZF.rate,
      count: resultsZF.count,
    });
  }

  if (resultsBilan.count > 0) {
    rateRows.push({
      label: "Bilan",
      rate: resultsBilan.rate,
      count: resultsBilan.count,
    });
  }

  if (resultsZPCCF.count > 0) {
    rateRows.push({
      label: "Couleurs",
      rate: resultsZPCCF.rate,
      count: resultsZPCCF.count,
    });
  }

  if (resultsZPFCF.count > 0) {
    rateRows.push({
      label: "Formes",
      rate: resultsZPFCF.rate,
      count: resultsZPFCF.count,
    });
  }

  // Collect Score Rows
  const scoreRows: ScoreRow[] = [];

  const obScore = formatScore(session.resultsOB);
  if (obScore !== "Non réalisé") {
    scoreRows.push({
      label: "Lecture Barres",
      score: obScore,
    });
  }

  const otScore = formatScore(session.resultsOT);
  if (otScore !== "Non réalisé") {
    scoreRows.push({
      label: "Lecture Texte",
      score: otScore,
    });
  }

  const pvjScore = formatScore(session.resultsPVJ);
  if (pvjScore !== "Non réalisé") {
    scoreRows.push({
      label: "Poursuite Visuelle Jump",
      score: pvjScore,
    });
  }

  const pvsScore = formatScore(session.resultsPVS);
  if (pvsScore !== "Non réalisé") {
    scoreRows.push({
      label: "Poursuite Visuelle Smooth",
      score: pvsScore,
    });
  }

  const zpiScore = formatScore(session.resultsZPI);
  if (zpiScore !== "Non réalisé") {
    scoreRows.push({
      label: "Identification",
      score: zpiScore,
    });
  }

  const zplScore = formatScore(session.resultsZPL);
  if (zplScore !== "Non réalisé") {
    scoreRows.push({
      label: "Localisation",
      score: zplScore,
    });
  }

  if (bilanLectureTime !== null) {
    scoreRows.push({
      label: "Bilan Lecture",
      score: `${bilanLectureTime.toFixed(2)} sec`,
    });
  }

  // Collect Exploration Rows
  const explorationRows: ExplorationResultRow[] = [];

  if (
    resultsBilanExploration.success ||
    resultsBilanExploration.failureCount > 0
  ) {
    explorationRows.push({
      label: "Bilan Exploration",
      success: resultsBilanExploration.success,
      failureCount: resultsBilanExploration.failureCount,
      totalTime: resultsBilanExploration.totalTime,
    });
  }

  // Create Sections
  const sections: Section[] = [];

  if (rateRows.length > 0) {
    sections.push({
      title: "Exercices avec taux de réussite",
      headers: ["Type", "Taux de réussite", "Nombre de cibles"],
      rows: rateRows,
      rowType: 'rate',
    });
  }

  if (scoreRows.length > 0) {
    sections.push({
      title: "Exercice avec score",
      headers: ["Type", "Score"],
      rows: scoreRows,
      rowType: 'score',
    });
  }

  if (explorationRows.length > 0) {
    sections.push({
      title: "Bilan Exploration",
      headers: ["Type", "Résultats", "Nombre d'échecs"],
      rows: explorationRows,
      rowType: 'exploration',
    });
  }

  const accountUsedLabel = accountUsed === "PATIENT" ? "Patient" : "Clinique";
  const sessionTypeLabel = sessionType === "BILAN" ? "Bilan" : "Rééducation";
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" aria-label="Voir les résultats">
          <FileBarChart2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            Séance de {sessionTypeLabel} n° {order} - Compte {accountUsedLabel}
            <p className={fiableClass}>{textFiable}</p>
          </DialogTitle>
        </DialogHeader>
        {sections.map((section, idx) => (
          <div key={idx}>
            <h2 className="text-lg font-bold mt-4">{section.title}</h2>
            <Table>
              <TableHeader>
                <TableRow>
                  {section.headers.map((header, idx2) => (
                    <TableHead key={idx2}>{header}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {section.rows.map((row, index) => {
                  if (section.rowType === 'score') {
                    const scoreRow = row as ScoreRow;
                    return (
                      <TableRow key={index}>
                        <TableCell>{scoreRow.label}</TableCell>
                        <TableCell>{scoreRow.score}</TableCell>
                      </TableRow>
                    );
                  } else if (section.rowType === 'rate') {
                    const rateRow = row as RateRow;
                    return (
                      <TableRow key={index}>
                        <TableCell>{rateRow.label}</TableCell>
                        <TableCell>{rateRow.rate.toFixed(2)}%</TableCell>
                        <TableCell>{rateRow.count}</TableCell>
                      </TableRow>
                    );
                  } else if (section.rowType === 'exploration') {
                    const explorationRow = row as ExplorationResultRow;
                    return (
                      <TableRow key={index}>
                        <TableCell>{explorationRow.label}</TableCell>
                        <TableCell>
                          {explorationRow.success ? "Succès" : "Échec"}
                          {explorationRow.totalTime !== null
                            ? ` - ${explorationRow.totalTime} ms`
                            : ""}
                        </TableCell>
                        <TableCell>{explorationRow.failureCount}</TableCell>
                      </TableRow>
                    );
                  }
                  return null;
                })}
              </TableBody>
            </Table>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};
